// src/pages/Users/News/components/NewsFeaturedCarousel.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarAlt,
  FaExpandAlt,
  FaNewspaper,
  FaUserEdit,
} from "react-icons/fa";

import NewsImageModal from "./NewsImageModal";

import {
  FALLBACK_NEWS_IMG,
  formatNewsDate,
  getAuthorName,
  getNewsCover,
  getNewsDate,
  sanitizeLight,
} from "../utils/newsUtils";

function onImgError(event) {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK_NEWS_IMG;
  }
}

export default function NewsFeaturedCarousel({ items = [] }) {
  const [index, setIndex] = useState(0);
  const [imageOpen, setImageOpen] = useState(false);

  const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];
  const current = safeItems[index] || safeItems[0];

  useEffect(() => {
    if (index > safeItems.length - 1) {
      setIndex(0);
    }
  }, [index, safeItems.length]);

  const contentHtml = useMemo(() => {
    const html = sanitizeLight(current?.content || "");

    return html || '<p class="italic text-slate-500">Contenu non renseigné.</p>';
  }, [current?.content]);

  if (!current) return null;

  const cover = getNewsCover(current);
  const author = getAuthorName(current);
  const date = formatNewsDate(getNewsDate(current));

  const previous = () => {
    setIndex((prev) => (prev - 1 + safeItems.length) % safeItems.length);
  };

  const next = () => {
    setIndex((prev) => (prev + 1) % safeItems.length);
  };

  return (
    <>
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-950/5 dark:border-slate-800 dark:bg-slate-900">
        <div className="grid min-h-[520px] lg:grid-cols-[0.95fr_1.05fr]">
          <div className="relative min-h-[340px] overflow-hidden bg-slate-950 lg:min-h-[520px]">
            <img
              src={cover}
              alt={current?.title || "Actualité"}
              className="h-full w-full object-cover"
              loading="eager"
              onError={onImgError}
            />

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/15 to-transparent" />

            <button
              type="button"
              onClick={() => setImageOpen(true)}
              className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition active:scale-95"
              aria-label="Agrandir la photo"
              title="Agrandir la photo"
            >
              <FaExpandAlt />
            </button>

            {safeItems.length > 1 && (
              <button
                type="button"
                onClick={previous}
                className="absolute left-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-950 shadow-lg transition active:scale-95"
                aria-label="Actualité précédente"
              >
                <FaArrowLeft />
              </button>
            )}

            <div className="absolute bottom-5 left-5 right-5">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white backdrop-blur">
                <FaUserEdit />
                {author}
              </div>

              <h2 className="text-3xl font-bold leading-tight text-white sm:text-4xl">
                {current?.title || "Actualité du club"}
              </h2>
            </div>
          </div>

          <div className="relative flex flex-col p-5 sm:p-7 lg:p-9">
            <div className="flex items-start justify-between gap-4">
              <span className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-orange-600 dark:border-orange-900/50 dark:bg-orange-950/30 dark:text-orange-300">
                <FaNewspaper />
                Article du club
              </span>

              <span className="rounded-full bg-white px-4 py-2 text-xs font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700">
                {index + 1} / {safeItems.length}
              </span>
            </div>

            <h3 className="mt-7 text-3xl font-bold leading-tight text-slate-950 dark:text-white sm:text-4xl">
              {current?.title || "Actualité du club"}
            </h3>

            <div className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-400 dark:ring-slate-800">
              <FaCalendarAlt />
              {date}
            </div>

            <div
              className="
                mt-5 max-h-[210px] overflow-y-auto pr-2 text-base leading-8 text-slate-600 dark:text-slate-300
                prose prose-slate max-w-none dark:prose-invert
                prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-headings:my-2
                prose-a:text-orange-600 dark:prose-a:text-orange-300
              "
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />

            <div className="mt-auto pt-7">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  {safeItems.map((item, dotIndex) => (
                    <button
                      key={item.id || dotIndex}
                      type="button"
                      onClick={() => setIndex(dotIndex)}
                      className={`h-2.5 rounded-full transition ${
                        dotIndex === index
                          ? "w-8 bg-orange-500"
                          : "w-2.5 bg-slate-300 dark:bg-slate-700"
                      }`}
                      aria-label={`Afficher l’actualité ${dotIndex + 1}`}
                    />
                  ))}
                </div>

                {safeItems.length > 1 && (
                  <button
                    type="button"
                    onClick={next}
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg ring-1 ring-slate-200 transition active:scale-95 dark:bg-slate-950 dark:text-white dark:ring-slate-700"
                    aria-label="Actualité suivante"
                  >
                    <FaArrowRight />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <NewsImageModal
        isOpen={imageOpen}
        imageUrl={cover}
        title={current?.title}
        onClose={() => setImageOpen(false)}
      />
    </>
  );
}